// ImageCropModal — recortar/rotacionar uma imagem (data URL) sem dependência
// externa. Canvas + pointer events; saída em PNG (preserva transparência) ou
// JPEG se o admin escolher comprimir.
//
// Uso:
//   const [cropping, setCropping] = useState<string | null>(null);
//   ...
//   <button onClick={() => setCropping(currentDataUrl)}>Recortar</button>
//   {cropping && (
//     <ImageCropModal
//       src={cropping}
//       onConfirm={(next) => { setCropping(null); save(next); }}
//       onCancel={() => setCropping(null)}
//     />
//   )}
//
// Fluxo: a imagem renderiza dentro de uma área de canvas com fit-to-box; o
// admin arrasta as bordas/cantos da seleção (rect handles) e move o retângulo
// inteiro arrastando o miolo. Rotacionar 90° é um botão dedicado; aplica
// transform no canvas-fonte antes de exportar.

import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import {
  Check,
  Crop,
  Loader2,
  Maximize2,
  RotateCw,
  Trash2,
  X
} from "lucide-react";

interface ImageCropModalProps {
  src: string;
  onConfirm: (nextDataUrl: string) => void;
  onCancel: () => void;
  /** Opcional: quando informado, renderiza um botão "Remover imagem" no rodapé
   *  do modal. Útil quando o modal abre via clique em imagem inline existente
   *  e o admin quer apagar ao invés de recortar. */
  onRemove?: () => void;
  // Largura máxima da imagem exportada — evita data URLs gigantes quando o
  // admin cola screenshot de 4K. Default 1600px.
  maxOutputWidth?: number;
  // Formato de saída. Default "image/png" pra preservar transparência; quando
  // a imagem original já é JPEG e o admin quer reduzir, "image/jpeg" cabe.
  mimeType?: "image/png" | "image/jpeg" | "image/webp";
  // Qualidade JPEG/webp (0..1). Default 0.92.
  quality?: number;
}

type Rect = { x: number; y: number; w: number; h: number };

type HandleKind =
  | "move"
  | "nw"
  | "n"
  | "ne"
  | "e"
  | "se"
  | "s"
  | "sw"
  | "w";

const HANDLE_HIT = 14; // px raio do hitbox dos cantos/bordas

export function ImageCropModal({
  src,
  onConfirm,
  onCancel,
  onRemove,
  maxOutputWidth = 1600,
  mimeType = "image/png",
  quality = 0.92
}: ImageCropModalProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const overlayRef = useRef<HTMLCanvasElement | null>(null);

  // Imagem fonte carregada. Quando rotaciona, redesenhamos a partir desse
  // bitmap aplicando rotação em um canvas off-screen e usando o resultado
  // como nova "fonte".
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [rotation, setRotation] = useState(0); // múltiplos de 90 (0/90/180/270)
  const [loadError, setLoadError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  // rect em coordenadas da imagem fonte (após rotação aplicada). Inicia
  // como retângulo cheio (sem recortar nada).
  const [rect, setRect] = useState<Rect | null>(null);

  // Dimensões do canvas visível (ajustadas ao container).
  const [view, setView] = useState<{ w: number; h: number }>({ w: 0, h: 0 });

  // Escala/offsets para mapear coords da imagem → coords do canvas.
  const fit = useMemo(() => {
    if (!img || !view.w || !view.h) return null;
    const iw = img.naturalWidth;
    const ih = img.naturalHeight;
    const scale = Math.min(view.w / iw, view.h / ih);
    const w = Math.round(iw * scale);
    const h = Math.round(ih * scale);
    const dx = Math.floor((view.w - w) / 2);
    const dy = Math.floor((view.h - h) / 2);
    return { scale, w, h, dx, dy, iw, ih };
  }, [img, view]);

  // Carrega imagem original.
  useEffect(() => {
    const im = new Image();
    im.onload = () => {
      setImg(im);
      setRect({ x: 0, y: 0, w: im.naturalWidth, h: im.naturalHeight });
    };
    im.onerror = () => setLoadError("Não foi possível carregar a imagem.");
    im.src = src;
  }, [src]);

  // Aplica rotação criando um novo bitmap rotacionado e o usando como fonte.
  // Reseta o rect pra cobrir o novo tamanho.
  function rotate90() {
    if (!img) return;
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    const c = document.createElement("canvas");
    c.width = h;
    c.height = w;
    const g = c.getContext("2d");
    if (!g) return;
    g.translate(c.width / 2, c.height / 2);
    g.rotate(Math.PI / 2);
    g.drawImage(img, -w / 2, -h / 2);
    const next = new Image();
    next.onload = () => {
      setImg(next);
      setRect({ x: 0, y: 0, w: next.naturalWidth, h: next.naturalHeight });
      setRotation((r) => (r + 90) % 360);
    };
    next.src = c.toDataURL("image/png");
  }

  // Mede container.
  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    function measure() {
      const r = el.getBoundingClientRect();
      // Mantém uma área de canvas com altura razoável; a UI cresce com o modal.
      const w = Math.max(280, Math.floor(r.width));
      const h = Math.max(280, Math.floor(Math.min(window.innerHeight * 0.62, w * 0.75)));
      setView({ w, h });
    }
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  // Render do canvas-base (imagem) e overlay (rect + dimmer + handles).
  useEffect(() => {
    if (!fit || !img || !canvasRef.current || !overlayRef.current) return;
    const base = canvasRef.current;
    const ov = overlayRef.current;
    base.width = view.w;
    base.height = view.h;
    ov.width = view.w;
    ov.height = view.h;
    const g = base.getContext("2d");
    if (!g) return;
    g.fillStyle = "#0b0b0b";
    g.fillRect(0, 0, view.w, view.h);
    g.drawImage(img, fit.dx, fit.dy, fit.w, fit.h);
  }, [img, fit, view]);

  // Render do overlay (rect+handles+dimmer). Re-renderiza quando rect muda.
  useEffect(() => {
    if (!fit || !overlayRef.current || !rect) return;
    const ov = overlayRef.current;
    const g = ov.getContext("2d");
    if (!g) return;
    g.clearRect(0, 0, ov.width, ov.height);
    // Coords do rect no canvas
    const rx = fit.dx + rect.x * fit.scale;
    const ry = fit.dy + rect.y * fit.scale;
    const rw = rect.w * fit.scale;
    const rh = rect.h * fit.scale;
    // Dimmer fora do rect (4 retângulos cobrindo a área fora).
    g.fillStyle = "rgba(0,0,0,0.55)";
    g.fillRect(0, 0, ov.width, ry);
    g.fillRect(0, ry + rh, ov.width, ov.height - (ry + rh));
    g.fillRect(0, ry, rx, rh);
    g.fillRect(rx + rw, ry, ov.width - (rx + rw), rh);
    // Borda do rect
    g.strokeStyle = "#8b5cf6";
    g.lineWidth = 2;
    g.strokeRect(rx + 0.5, ry + 0.5, rw - 1, rh - 1);
    // Guias rule-of-thirds
    g.strokeStyle = "rgba(255,255,255,0.35)";
    g.lineWidth = 1;
    for (let i = 1; i < 3; i++) {
      const gx = rx + (rw / 3) * i;
      g.beginPath();
      g.moveTo(gx + 0.5, ry);
      g.lineTo(gx + 0.5, ry + rh);
      g.stroke();
      const gy = ry + (rh / 3) * i;
      g.beginPath();
      g.moveTo(rx, gy + 0.5);
      g.lineTo(rx + rw, gy + 0.5);
      g.stroke();
    }
    // Handles
    g.fillStyle = "#8b5cf6";
    const s = 8;
    const drawHandle = (cx: number, cy: number) => {
      g.fillRect(cx - s / 2, cy - s / 2, s, s);
    };
    drawHandle(rx, ry);
    drawHandle(rx + rw / 2, ry);
    drawHandle(rx + rw, ry);
    drawHandle(rx, ry + rh / 2);
    drawHandle(rx + rw, ry + rh / 2);
    drawHandle(rx, ry + rh);
    drawHandle(rx + rw / 2, ry + rh);
    drawHandle(rx + rw, ry + rh);
  }, [rect, fit]);

  // Hit testing para definir cursor + qual ação iniciar no pointerdown.
  function hitTest(cx: number, cy: number): HandleKind | null {
    if (!fit || !rect) return null;
    const rx = fit.dx + rect.x * fit.scale;
    const ry = fit.dy + rect.y * fit.scale;
    const rw = rect.w * fit.scale;
    const rh = rect.h * fit.scale;
    const near = (px: number, py: number) =>
      Math.abs(cx - px) <= HANDLE_HIT && Math.abs(cy - py) <= HANDLE_HIT;
    if (near(rx, ry)) return "nw";
    if (near(rx + rw, ry)) return "ne";
    if (near(rx, ry + rh)) return "sw";
    if (near(rx + rw, ry + rh)) return "se";
    if (near(rx + rw / 2, ry)) return "n";
    if (near(rx + rw / 2, ry + rh)) return "s";
    if (near(rx, ry + rh / 2)) return "w";
    if (near(rx + rw, ry + rh / 2)) return "e";
    if (cx > rx && cx < rx + rw && cy > ry && cy < ry + rh) return "move";
    return null;
  }

  const dragging = useRef<{
    kind: HandleKind;
    startCx: number;
    startCy: number;
    startRect: Rect;
  } | null>(null);

  function onPointerDown(e: PointerEvent) {
    if (!fit || !rect || !overlayRef.current) return;
    const ov = overlayRef.current;
    const bb = ov.getBoundingClientRect();
    const cx = e.clientX - bb.left;
    const cy = e.clientY - bb.top;
    const k = hitTest(cx, cy);
    if (!k) return;
    (e.target as Element).setPointerCapture?.(e.pointerId);
    dragging.current = { kind: k, startCx: cx, startCy: cy, startRect: rect };
  }

  function onPointerMove(e: PointerEvent) {
    if (!fit || !overlayRef.current) return;
    const ov = overlayRef.current;
    const bb = ov.getBoundingClientRect();
    const cx = e.clientX - bb.left;
    const cy = e.clientY - bb.top;
    // Atualiza cursor mesmo sem drag.
    const hover = hitTest(cx, cy);
    ov.style.cursor = cursorFor(hover);
    if (!dragging.current || !rect) return;
    const dx = (cx - dragging.current.startCx) / fit.scale;
    const dy = (cy - dragging.current.startCy) / fit.scale;
    const r0 = dragging.current.startRect;
    let { x, y, w, h } = r0;
    const min = 8 / fit.scale; // mínimo de 8px no canvas pra não sumir
    switch (dragging.current.kind) {
      case "move":
        x = r0.x + dx;
        y = r0.y + dy;
        break;
      case "n":
        y = r0.y + dy;
        h = r0.h - dy;
        break;
      case "s":
        h = r0.h + dy;
        break;
      case "w":
        x = r0.x + dx;
        w = r0.w - dx;
        break;
      case "e":
        w = r0.w + dx;
        break;
      case "nw":
        x = r0.x + dx;
        y = r0.y + dy;
        w = r0.w - dx;
        h = r0.h - dy;
        break;
      case "ne":
        y = r0.y + dy;
        w = r0.w + dx;
        h = r0.h - dy;
        break;
      case "sw":
        x = r0.x + dx;
        w = r0.w - dx;
        h = r0.h + dy;
        break;
      case "se":
        w = r0.w + dx;
        h = r0.h + dy;
        break;
    }
    // Clampa nos limites da imagem fonte.
    if (w < min) w = min;
    if (h < min) h = min;
    if (x < 0) {
      w += x;
      x = 0;
    }
    if (y < 0) {
      h += y;
      y = 0;
    }
    if (x + w > fit.iw) w = fit.iw - x;
    if (y + h > fit.ih) h = fit.ih - y;
    setRect({ x, y, w, h });
  }

  function onPointerUp(e: PointerEvent) {
    dragging.current = null;
    (e.target as Element).releasePointerCapture?.(e.pointerId);
  }

  function resetCrop() {
    if (!img) return;
    setRect({ x: 0, y: 0, w: img.naturalWidth, h: img.naturalHeight });
  }

  function handleConfirm() {
    if (!img || !rect) return;
    setExporting(true);
    try {
      // Calcula escala de saída — limita ao maxOutputWidth.
      const outScale = rect.w > maxOutputWidth ? maxOutputWidth / rect.w : 1;
      const outW = Math.max(1, Math.round(rect.w * outScale));
      const outH = Math.max(1, Math.round(rect.h * outScale));
      const out = document.createElement("canvas");
      out.width = outW;
      out.height = outH;
      const g = out.getContext("2d");
      if (!g) {
        setExporting(false);
        return;
      }
      g.drawImage(
        img,
        rect.x,
        rect.y,
        rect.w,
        rect.h,
        0,
        0,
        outW,
        outH
      );
      const url = out.toDataURL(mimeType, quality);
      onConfirm(url);
    } finally {
      setExporting(false);
    }
  }

  // ESC fecha.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onCancel();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);

  return (
    <div class="fixed inset-0 z-[90] grid place-items-center bg-background/85 p-3 backdrop-blur-sm">
      <div class="flex max-h-[94vh] w-full max-w-3xl flex-col overflow-hidden rounded-md border border-border bg-card shadow-2xl">
        <header class="flex shrink-0 items-center justify-between border-b border-border px-4 py-3">
          <h2 class="flex items-center gap-2 text-base font-bold">
            <Crop width="1.2em" height="1.2em" className="text-primary" />
            Recortar imagem
          </h2>
          <button
            type="button"
            onClick={onCancel}
            title="Fechar"
            aria-label="Fechar"
            class="rounded-md border border-border bg-background p-1 hover:bg-accent"
          >
            <X width="1em" height="1em" />
          </button>
        </header>

        <div class="min-h-0 flex-1 space-y-2 overflow-y-auto p-4">
          {loadError ? (
            <div class="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              {loadError}
            </div>
          ) : (
            <>
              <div
                ref={containerRef}
                class="relative w-full overflow-hidden rounded-md border border-border bg-black/70"
                style={{ height: view.h ? `${view.h}px` : "60vh" }}
              >
                <canvas
                  ref={canvasRef}
                  width={view.w}
                  height={view.h}
                  class="block"
                />
                <canvas
                  ref={overlayRef}
                  width={view.w}
                  height={view.h}
                  class="absolute inset-0 touch-none"
                  onPointerDown={onPointerDown as any}
                  onPointerMove={onPointerMove as any}
                  onPointerUp={onPointerUp as any}
                  onPointerCancel={onPointerUp as any}
                />
                {!img && (
                  <div class="absolute inset-0 grid place-items-center text-xs text-muted-foreground">
                    <Loader2 className="size-4 animate-spin" />
                  </div>
                )}
              </div>

              <div class="flex flex-wrap items-center gap-2 text-xs">
                <button
                  type="button"
                  onClick={rotate90}
                  disabled={!img}
                  class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1 font-semibold hover:bg-accent disabled:opacity-50"
                  title="Girar 90°"
                >
                  <RotateCw className="size-3.5" /> Girar 90°
                </button>
                <button
                  type="button"
                  onClick={resetCrop}
                  disabled={!img}
                  class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1 font-semibold hover:bg-accent disabled:opacity-50"
                  title="Restaurar recorte para imagem inteira"
                >
                  <Maximize2 className="size-3.5" /> Imagem inteira
                </button>
                <span class="ml-auto font-mono text-[10px] text-muted-foreground">
                  {rect && img
                    ? `Recorte: ${Math.round(rect.w)} × ${Math.round(rect.h)} px${
                        rotation ? ` · rotação ${rotation}°` : ""
                      }`
                    : ""}
                </span>
              </div>
            </>
          )}
        </div>

        <div class="flex shrink-0 items-center justify-end gap-2 border-t border-border bg-card px-4 py-3">
          {onRemove && (
            <button
              type="button"
              onClick={onRemove}
              class="mr-auto inline-flex items-center gap-1.5 rounded-md border border-red-500/40 bg-red-500/5 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-500/10 dark:text-red-400"
              title="Apagar essa imagem da alternativa"
            >
              <Trash2 className="size-3.5" /> Remover imagem
            </button>
          )}
          <button
            type="button"
            onClick={onCancel}
            class="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!img || !rect || exporting || !!loadError}
            class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-white hover:brightness-110 disabled:opacity-50"
          >
            {exporting ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <Check className="size-3.5" />
            )}
            Aplicar recorte
          </button>
        </div>
      </div>
    </div>
  );
}

function cursorFor(k: HandleKind | null): string {
  switch (k) {
    case "move":
      return "move";
    case "n":
    case "s":
      return "ns-resize";
    case "e":
    case "w":
      return "ew-resize";
    case "ne":
    case "sw":
      return "nesw-resize";
    case "nw":
    case "se":
      return "nwse-resize";
    default:
      return "default";
  }
}
